import { JSONRecordData } from '../Log/graph.util';
import {
  Alert,
  Box,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  TextField,
  useTheme
} from '@mui/material';

import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@mui/material/Button';
import { Close as CloseIcon } from '@mui/icons-material';
import axiosConfig from '../../../Resources/axiosConfig';

interface ConfigurationSodistoreGridProps {
  values: JSONRecordData;
  id: number;
}

// Wire-format keys (camelCase → backend Configuration PascalCase via case-insensitive bind).
interface GridFormValues {
  minimumSoC?: string;
  activePowerPercent?: string;
  minDischargeVoltage?: string;
  maxDischargeCurrent?: string;
  maxChargeCurrent?: string;
  maxChargeVoltage?: string;
}

// Per-field validation rules.
// type="text" + regex (NOT type="number" — avoids spinner & locale issues, per project rule).
const FIELDS: {
  name: keyof GridFormValues;
  labelId: string;
  min?: number;
  max?: number;
  allowNegative?: boolean;
}[] = [
  { name: 'minimumSoC',          labelId: 'minimumSocPercent',     min: 0, max: 100 },
  { name: 'activePowerPercent',  labelId: 'activePowerPercent',    min: -100, max: 100, allowNegative: true },
  { name: 'minDischargeVoltage', labelId: 'minDischargeVoltageV',  min: 0 },
  { name: 'maxDischargeCurrent', labelId: 'maxDischargeCurrentA',  min: 0 },
  { name: 'maxChargeCurrent',    labelId: 'maxChargeCurrentA',     min: 0 },
  { name: 'maxChargeVoltage',    labelId: 'maxChargeVoltageV',     min: 0 }
];

function ConfigurationSodistoreGrid(props: ConfigurationSodistoreGridProps) {
  const intl = useIntl();
  const theme = useTheme();

  // All hooks must be called unconditionally (Rules of Hooks). The null-check
  // moved BELOW the hook declarations.
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [updated, setUpdated] = useState(false);

  // Lazy initializer reads from props.values safely even when null.
  const [formValues, setFormValues] = useState<GridFormValues>(() => {
    const inv: any = props.values?.InverterRecord ?? {};
    const cfg: any = props.values?.Config ?? {};
    return {
      minimumSoC:          cfg.MinSoc != null ? String(cfg.MinSoc) : '',
      activePowerPercent:  cfg.ActivePowerPercent != null ? String(cfg.ActivePowerPercent) : '',
      minDischargeVoltage: inv.MinDischargeVoltage != null ? String(inv.MinDischargeVoltage) : '',
      maxDischargeCurrent: inv.MaxDischargeCurrent != null ? String(inv.MaxDischargeCurrent) : '',
      maxChargeCurrent:    inv.MaxChargeCurrent != null ? String(inv.MaxChargeCurrent) : '',
      maxChargeVoltage:    inv.MaxChargeVoltage != null ? String(inv.MaxChargeVoltage) : ''
    };
  });

  const [errors, setErrors] = useState<Record<keyof GridFormValues, boolean>>({
    minimumSoC: false,
    activePowerPercent: false,
    minDischargeVoltage: false,
    maxDischargeCurrent: false,
    maxChargeCurrent: false,
    maxChargeVoltage: false
  });

  if (props.values === null) {
    return null;
  }

  const validate = (
    field: (typeof FIELDS)[number],
    raw: string
  ): boolean => {
    if (raw.trim() === '') return false; // empty = "leave unchanged", not error
    // Regex: allow leading minus only if field accepts negatives, optional digits + dot
    const pattern = field.allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
    if (!pattern.test(raw)) return true;
    const n = parseFloat(raw);
    if (isNaN(n)) return true;
    if (field.min !== undefined && n < field.min) return true;
    if (field.max !== undefined && n > field.max) return true;
    return false;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const field = FIELDS.find((f) => f.name === name);
    if (!field) return;

    setErrors((prev) => ({ ...prev, [name]: validate(field, value) }));
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const anyError = Object.values(errors).some(Boolean);
  const allEmpty = Object.values(formValues).every(
    (v) => !v || v.trim() === ''
  );

  const handleSubmit = async () => {
    // Only send fields the user actually entered. Empty string = skip.
    const payload: Record<string, number> = {};
    FIELDS.forEach((f) => {
      const raw = formValues[f.name];
      if (raw && raw.trim() !== '') {
        const n = parseFloat(raw);
        if (!isNaN(n)) payload[f.name] = n;
      }
    });

    if (Object.keys(payload).length === 0) return;

    setLoading(true);
    setError(false);
    setUpdated(false);

    try {
      await axiosConfig.post(
        `/EditInstallationConfig?installationId=${props.id}&product=4`,
        payload
      );
      setUpdated(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const helperFor = (name: keyof GridFormValues, hasError: boolean) => {
    if (!hasError) return '';
    const f = FIELDS.find((fld) => fld.name === name)!;
    // Range-aware helper: 0–100 → existing key; otherwise generic.
    if (f.min === 0 && f.max === 100) {
      return (
        <span style={{ color: 'red' }}>
          {intl.formatMessage({ id: 'valueBetween0And100' })}
        </span>
      );
    }
    return (
      <span style={{ color: 'red' }}>
        {intl.formatMessage({ id: 'pleaseProvideValidNumber' })}
      </span>
    );
  };

  return (
    <Container maxWidth="xl">
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={3}
      >
        <Grid item xs={12} md={12}>
          <CardContent>
            <Box
              component="form"
              sx={{ '& .MuiTextField-root': { m: 1, width: 390 } }}
              noValidate
              autoComplete="off"
              onSubmit={(e) => {
                // Prevent native form submit (Enter in TextField would navigate).
                e.preventDefault();
                handleSubmit();
              }}
            >
              {FIELDS.map((f) => (
                <div key={f.name} style={{ marginBottom: '5px' }}>
                  <TextField
                    type="text"
                    label={<FormattedMessage id={f.labelId} />}
                    name={f.name}
                    value={formValues[f.name] ?? ''}
                    onChange={handleChange}
                    error={errors[f.name]}
                    helperText={helperFor(f.name, errors[f.name])}
                    fullWidth
                  />
                </div>
              ))}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: 10
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={anyError || loading || allEmpty}
                  sx={{ marginLeft: '10px' }}
                >
                  <FormattedMessage
                    id="applyChanges"
                    defaultMessage="Apply changes"
                  />
                </Button>

                {loading && (
                  <CircularProgress
                    sx={{
                      color: theme.palette.primary.main,
                      marginLeft: '20px'
                    }}
                  />
                )}

                {updated && (
                  <Alert
                    severity="success"
                    sx={{ ml: 1, display: 'flex', alignItems: 'center' }}
                  >
                    <FormattedMessage
                      id="successfullyAppliedConfig"
                      defaultMessage="Successfully applied configuration file"
                    />
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={() => setUpdated(false)}
                      sx={{ marginLeft: '4px' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Alert>
                )}

                {error && (
                  <Alert
                    severity="error"
                    sx={{ ml: 1, display: 'flex', alignItems: 'center' }}
                  >
                    <FormattedMessage
                      id="configErrorOccurred"
                      defaultMessage="An error has occurred"
                    />
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={() => setError(false)}
                      sx={{ marginLeft: '4px' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Alert>
                )}
              </div>
            </Box>
          </CardContent>
        </Grid>
      </Grid>
    </Container>
  );
}

export default ConfigurationSodistoreGrid;
